import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { DraftPickLog } from '@/types/draftPlayer';
import { cn } from '@/lib/utils';
import { Bomb } from 'lucide-react';
import { ActiveNewsProvider } from '@/components/player/ActiveNewsContext';
import PlayerNewsIcon from '@/components/player/PlayerNewsIcon';

interface DraftOrderEntry {
  id: string;
  team_id: string;
  round: number;
  pick_number: number;
  round_pick: number;
}

interface DraftBoardProps {
  picks: DraftPickLog[];
  teams: { id: string; name: string; owner: string }[];
  totalRounds: number;
  currentPick: number;
  viewMode: 'grid' | 'list';
  draftOrder?: DraftOrderEntry[];
  skippedPickNumbers?: Set<number>;
  /** When true, the board fills its parent height instead of using a fixed h-[400px] */
  fillHeight?: boolean;
  /** When true, renders in expanded modal mode with wider columns and full names */
  expanded?: boolean;
  /**
   * SAB-847: resolve a position's colour classes. Supplied by the caller so each sport
   * keeps ONE palette instead of this file growing a second one.
   *
   * NFL passes its own resolver, which is aligned with nfl_slot_eligibility and folds all
   * 13 IDP variants onto a single colour. Adding those keys to `positionColors` below
   * would have created a second NFL palette free to drift from the eligibility table --
   * the duplication SAB-739 exists to prevent.
   *
   * Absent, the built-in `positionColors` map is used exactly as before, so MLB is
   * unchanged. Note the built-in lookup can miss: `positionColors[pos] + " border"` on an
   * unknown position yields the literal className "undefined border" -- no colour and no
   * error. A resolver is expected to always return a real class string.
   */
  colorFor?: (position: string) => string;
  /**
   * SAB-847: render the richer cell -- player name, position, NFL team, bye week and a
   * round.pick label -- plus a snake-direction arrow per round. Off by default, so MLB
   * keeps the name-only cell it has always had.
   */
  detailedCells?: boolean;
  /**
   * SAB-845: render UNPICKED cells empty instead of showing their pick number or "OTC".
   *
   * Those numbers come from a (round, team) mapping that is stale while an order-rewriting
   * sabotage is live, so the cell would assert that a specific team owns a specific
   * upcoming slot -- exactly what the sabotage changed. Made picks are unaffected (their
   * rows were rewritten at pick time), so history stays readable. Off by default.
   */
  blankUnpicked?: boolean;
}

const positionColors: Record<string, string> = {
  QB: 'bg-red-500/20 text-red-400 border-red-500/30',
  RB: 'bg-green-500/20 text-green-400 border-green-500/30',
  WR: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  TE: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  K: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  DEF: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  C: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  '1B': 'bg-green-500/20 text-green-400 border-green-500/30',
  '2B': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  '3B': 'bg-red-500/20 text-red-400 border-red-500/30',
  SS: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  OF: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  SP: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  RP: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  DH: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  UTIL: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
};

const DraftBoard = ({ picks, teams, totalRounds, currentPick, viewMode, draftOrder, skippedPickNumbers, fillHeight, expanded, colorFor, detailedCells, blankUnpicked }: DraftBoardProps) => {
  if (viewMode === 'list') {
    return <DraftListView picks={picks} currentPick={currentPick} skippedPickNumbers={skippedPickNumbers} fillHeight={fillHeight} />;
  }

  return <DraftGridView picks={picks} teams={teams} totalRounds={totalRounds} currentPick={currentPick} draftOrder={draftOrder} skippedPickNumbers={skippedPickNumbers} fillHeight={fillHeight} expanded={expanded} colorFor={colorFor} detailedCells={detailedCells} blankUnpicked={blankUnpicked} />;
};

const DraftListView = ({ picks, currentPick, skippedPickNumbers, fillHeight }: { picks: DraftPickLog[]; currentPick: number; skippedPickNumbers?: Set<number>; fillHeight?: boolean }) => {
  const sortedPicks = [...picks].sort((a, b) => b.pickNumber - a.pickNumber);

  return (
    <ActiveNewsProvider sport="mlb" keys={sortedPicks.map(p => p.playerName)}>
    <Card className={cn("glass-card", fillHeight && "h-full flex flex-col")}>
      <CardHeader className="pb-3 shrink-0">
        <CardTitle className="text-lg flex items-center gap-2">
          📋 Pick History
          <Badge variant="outline" className="ml-auto">{picks.length} picks</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className={cn("p-0", fillHeight && "flex-1 min-h-0")}>
        <ScrollArea className={fillHeight ? "h-full" : "h-[400px]"}>
          <div className="space-y-1 p-4 pt-0">
            {sortedPicks.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>Draft hasn't started yet</p>
                <p className="text-sm">Picks will appear here</p>
              </div>
            ) : (
              sortedPicks.map((pick) => {
                const isSkipped = skippedPickNumbers?.has(pick.pickNumber);
                return (
                <div
                  key={pick.pickNumber}
                  className={cn(
                    "flex items-center gap-3 p-3 rounded-lg transition-all",
                    isSkipped
                      ? "bg-red-500/10 border border-red-500/30 border-dashed"
                      : pick.pickNumber === currentPick - 1
                        ? "bg-primary/20 border border-primary/30"
                        : "bg-muted/30"
                  )}
                >
                  <div className={cn(
                    "w-10 h-10 rounded-lg flex items-center justify-center font-mono font-bold text-sm",
                    isSkipped ? "bg-red-500/20" : "bg-muted"
                  )}>
                    {isSkipped ? <Bomb className="w-5 h-5 text-red-400" /> : pick.pickNumber}
                  </div>
                  <div className="flex-1 min-w-0">
                    {isSkipped ? (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-red-400">Pick Skipped</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {pick.teamName} • Round {pick.round}, Pick {pick.pickInRound}
                          <span className="text-red-400/70 ml-1">, sabotaged</span>
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <Badge className={cn("text-xs", positionColors[pick.position])}>
                            {pick.position}
                          </Badge>
                          <span className="font-medium truncate">{pick.playerName}</span>
                          <PlayerNewsIcon playerName={pick.playerName} />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {pick.teamName} • Round {pick.round}, Pick {pick.pickInRound}
                          {pick.wasAutoPick && <span className="text-warning ml-1">(Auto)</span>}
                          {pick.wasCommissionerPick && <span className="text-blue-400 ml-1">(Commish)</span>}
                          {pick.sabotagedBy && (
                            <span className="text-damage ml-1">💣 {pick.sabotageType}</span>
                          )}
                        </p>
                      </>
                    )}
                  </div>
                </div>
                );
              })
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
    </ActiveNewsProvider>
  );
};

const DraftGridView = ({
  picks,
  teams,
  totalRounds,
  currentPick,
  draftOrder,
  skippedPickNumbers,
  fillHeight,
  expanded,
  colorFor,
  detailedCells,
  blankUnpicked,
}: {
  picks: DraftPickLog[];
  teams: { id: string; name: string; owner: string }[];
  totalRounds: number;
  currentPick: number;
  draftOrder?: DraftOrderEntry[];
  skippedPickNumbers?: Set<number>;
  fillHeight?: boolean;
  expanded?: boolean;
  colorFor?: (position: string) => string;
  detailedCells?: boolean;
  blankUnpicked?: boolean;
}) => {
  // SAB-847: one place the cell colour is resolved. Falls back to the built-in map so a
  // caller that passes no resolver behaves exactly as before.
  const resolveColor = (position: string) => colorFor
    ? colorFor(position)
    : positionColors[position];
  // Sort teams by Round 1 draft order so columns match actual draft position
  const sortedTeams = draftOrder && draftOrder.length > 0
    ? (() => {
        const round1 = draftOrder.filter(o => o.round === 1).sort((a, b) => a.pick_number - b.pick_number);
        const ordered = round1.map(o => teams.find(t => t.id === o.team_id)).filter(Boolean) as typeof teams;
        // Append any teams not in draft order (shouldn't happen, but safety)
        const orderedIds = new Set(ordered.map(t => t.id));
        for (const t of teams) {
          if (!orderedIds.has(t.id)) ordered.push(t);
        }
        return ordered;
      })()
    : teams;

  // Look up pick_number for a given (round, columnIndex) using actual draft_order
  const getPickNumberForCell = (round: number, colIndex: number): number => {
    if (draftOrder && draftOrder.length > 0) {
      const teamId = sortedTeams[colIndex]?.id;
      const entry = draftOrder.find(o => o.round === round && o.team_id === teamId);
      if (entry) return entry.pick_number;
    }
    // Fallback: standard snake math
    const pickInRound = round % 2 === 1 ? colIndex + 1 : sortedTeams.length - colIndex;
    return (round - 1) * sortedTeams.length + pickInRound;
  };

  const getPickForCell = (round: number, colIndex: number) => {
    const pickNumber = getPickNumberForCell(round, colIndex);
    return picks.find(p => p.pickNumber === pickNumber);
  };

  const getCurrentPickNumber = (round: number, colIndex: number) => {
    return getPickNumberForCell(round, colIndex);
  };

  return (
    <Card className={cn("glass-card", fillHeight && "h-full flex flex-col")}>
      <CardHeader className="pb-3 shrink-0">
        <CardTitle className="text-lg flex items-center gap-2">
          📊 Draft Board
          <Badge variant="outline" className="ml-auto">{picks.length} / {teams.length * totalRounds}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className={cn("p-0", fillHeight && "flex-1 min-h-0 overflow-hidden")}>
        <div className={cn(
          "overflow-y-auto",
          fillHeight ? "h-full" : "h-[400px]"
        )}>
          <div className={cn("p-4 pt-0", (expanded || detailedCells) && "overflow-x-auto")}>
            {/* Header row with team names */}
            <div className={cn(
              "flex mb-2 sticky top-0 bg-background/95 backdrop-blur-sm py-2 z-10",
              (expanded || detailedCells) ? "gap-1" : "gap-0.5"
            )}>
              <div className={cn("shrink-0 text-xs font-medium text-muted-foreground", (expanded || detailedCells) ? "w-10" : "w-8")}>Rd</div>
              {sortedTeams.map((team) => (
                <div
                  key={team.id}
                  className={cn(
                    "font-medium text-center px-0.5",
                    detailedCells
                      ? "min-w-[132px] flex-1 text-xs truncate"
                      : expanded
                        ? "min-w-[120px] flex-1 text-xs"
                        : "flex-1 min-w-0 text-[10px] truncate"
                  )}
                  title={team.name}
                >
                  {(expanded || detailedCells) ? team.name : (team.name.length > 8 ? team.name.slice(0, 7) + '…' : team.name)}
                </div>
              ))}
            </div>

            {/* Grid rows */}
            {Array.from({ length: totalRounds }, (_, roundIndex) => {
              const round = roundIndex + 1;
              return (
                <div key={round} className={cn("flex mb-0.5", (expanded || detailedCells) ? "gap-1" : "gap-0.5")}>
                  <div className={cn(
                    "shrink-0 flex items-center justify-center text-xs font-mono text-muted-foreground",
                    (expanded || detailedCells) ? "w-10" : "w-8"
                  )}>
                    {/* SAB-847: snake flow. Odd rounds run left-to-right, even rounds
                        reverse, which is what makes the column under your name move. The
                        arrow states the BASE snake parity; an order-rewriting sabotage can
                        change the real direction, which is why NFL blanks unpicked cells
                        outright while one is live (see SAB-845). */}
                    {detailedCells ? (
                      <span className="flex flex-col items-center leading-none gap-0.5">
                        <span>{round}</span>
                        <span
                          aria-hidden="true"
                          className="text-[9px] text-muted-foreground/60"
                        >
                          {round % 2 === 1 ? '→' : '←'}
                        </span>
                      </span>
                    ) : round}
                  </div>
                  {sortedTeams.map((_, teamIndex) => {
                    const pick = getPickForCell(round, teamIndex);
                    const pickNum = getCurrentPickNumber(round, teamIndex);
                    const isCurrent = pickNum === currentPick;
                    const isSkipped = skippedPickNumbers?.has(pickNum);

                    return (
                      <div
                        key={`${round}-${teamIndex}`}
                        className={cn(
                          "rounded-md flex items-center justify-center transition-all overflow-hidden",
                          detailedCells
                            ? "min-w-[132px] flex-1 h-[58px] text-[11px]"
                            : expanded
                              ? "min-w-[120px] flex-1 h-12 text-xs"
                              : "flex-1 min-w-0 h-10 text-[10px]",
                          pick
                            ? resolveColor(pick.position) + " border"
                            : isSkipped
                              ? "bg-red-500/15 border border-red-500/50 border-dashed shadow-[inset_0_0_8px_rgba(239,68,68,0.15)]"
                              : isCurrent
                                ? "bg-primary/30 border-2 border-primary animate-pulse"
                                : "bg-muted/20 border border-transparent"
                        )}
                      >
                        {pick && detailedCells ? (
                          /* SAB-847: the Sleeper-style cell. Three lines inside the
                             position-coloured box: name, then position + NFL team, then
                             bye and the round.pick label. nflTeam/byeWeek are optional on
                             DraftPickLog, so each degrades to a dash on its own rather
                             than collapsing the row. */
                          <div className="w-full px-1.5 text-left leading-tight" title={pick.playerName}>
                            <div className="font-semibold truncate">{pick.playerName}</div>
                            <div className="flex items-center gap-1 text-[10px] opacity-90">
                              <span className="font-medium">{pick.position}</span>
                              <span className="opacity-70 truncate">{pick.nflTeam || '—'}</span>
                            </div>
                            <div className="flex items-center justify-between text-[9px] opacity-70">
                              <span>{pick.byeWeek != null ? `BYE ${pick.byeWeek}` : 'BYE —'}</span>
                              <span className="font-mono">{pick.round}.{pick.pickInRound}</span>
                            </div>
                          </div>
                        ) : pick ? (
                          <div className={cn("text-center w-full", expanded ? "px-1.5" : "px-0.5 truncate")} title={pick.playerName}>
                            <div className={cn("font-medium", !expanded && "truncate")}>
                              {expanded ? pick.playerName : pick.playerName.split(' ').pop()}
                            </div>
                          </div>
                        ) : isSkipped ? (
                          <div className="flex flex-col items-center gap-0.5">
                            <Bomb className="w-3 h-3 text-red-400" />
                            <span className="text-red-400 font-bold text-[8px] uppercase">Skip</span>
                          </div>
                        ) : blankUnpicked ? (
                          /* SAB-845: say nothing rather than name a slot the sabotage moved. */
                          null
                        ) : isCurrent ? (
                          <span className="text-primary font-bold text-[9px]">OTC</span>
                        ) : (
                          <span className="text-muted-foreground/50">{pickNum}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DraftBoard;
